import { useMemo } from 'react'
import * as React from 'react'
import { extractCookie, getCookies as readDocumentCookies } from 'lib/web/cookieUtils'
import { EmptyObject } from 'lib/object/objectUtils'

interface CookiesContextValue {
  getCookie: (name: string) => string | undefined
  getCookies: () => Record<string, string>
}

const getCookieNoop = () => undefined

function readBrowserCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {}
  return readDocumentCookies()
}

function readProvidedCookies(cookies: { [name: string]: string | undefined }): Record<string, string> {
  return Object.entries(cookies).reduce<Record<string, string>>((record, [name, value]) => {
    if (value !== undefined) record[name] = value
    return record
  }, {})
}

/**
 * Isomorphic way for components to get cookies, so any conditional rendering based on cookies values
 * will work on the server as well as the client.
 */
export const CookiesContext = React.createContext<CookiesContextValue>({
  getCookie: getCookieNoop,
  getCookies: readBrowserCookies,
})

interface Props {
  /** Only needs to be supplied on the server */
  cookies?: { [name: string]: string | undefined }
}

export function CookiesContextProvider({ cookies = EmptyObject, children }: React.PropsWithChildren<Props>) {
  const value = useMemo(() => {
    let getCookie: (name: string) => string | undefined
    let getCookies: () => Record<string, string>
    if (typeof document !== 'undefined') {
      getCookie = (name: string) => extractCookie(document.cookie, name)
      getCookies = readBrowserCookies
    } else {
      getCookie = (name: string) => cookies[name]
      getCookies = () => readProvidedCookies(cookies)
    }

    return { getCookie, getCookies }
  }, [cookies])

  return <CookiesContext value={value}>
    {children}
  </CookiesContext>
}

export default CookiesContext
